import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Button,
  CssBaseline,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  LocalFireDepartment as FireIcon,
  Assessment as ReportsIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Receipt as ReceiptIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Factory as FactoryIcon,
  ExitToApp as LogoutIcon,
  Description as DocumentIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

const MainLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    rawMaterial: false,
    heatTreatment: false,
    reports: false,
    masters: false
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSectionToggle = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard'
    },
    {
      title: 'Raw Material',
      icon: <InventoryIcon />,
      section: 'rawMaterial',
      subItems: [
        {
          title: 'Data Entry',
          items: [
            { title: 'Steel Receipt Report', path: '/raw-material/srr', icon: <ReceiptIcon /> },
            { title: 'Mill Test Certificate', path: '/raw-material/mtc', icon: <ScienceIcon /> },
            { title: 'Mechanical Properties', path: '/raw-material/mechanical', icon: <EngineeringIcon /> },
            { title: 'Production Steel Issue', path: '/raw-material/psis', icon: <AssignmentIcon /> },
            { title: 'RM Status Update', path: '/raw-material/update', icon: <StorageIcon /> },
            { title: 'KFL Chemistry', path: '/raw-material/chemistry', icon: <ScienceIcon /> }
          ]
        },
        {
          title: 'View/Print',
          items: [
            { title: 'View SRR', path: '/raw-material/view-srr', icon: <DocumentIcon /> },
            { title: 'View MTC', path: '/raw-material/view-mtc', icon: <DocumentIcon /> },
            { title: 'RM Status Report', path: '/raw-material/status', icon: <AssessmentIcon /> },
            { title: 'RM Testing Report', path: '/reports/rm-testing', icon: <ScienceIcon /> }
          ]
        }
      ]
    },
    {
      title: 'Heat Treatment',
      icon: <FireIcon />,
      section: 'heatTreatment',
      subItems: [
        {
          title: 'Data Entry',
          items: [
            { title: 'HT Production', path: '/heat-treatment/production', icon: <FactoryIcon /> },
            { title: 'Daily Production', path: '/heat-treatment/daily', icon: <AssignmentIcon /> }
          ]
        },
        {
          title: 'View/Print',
          items: [
            { title: 'HT Production Reports', path: '/heat-treatment/reports', icon: <ReportsIcon /> },
            { title: 'Rework Analysis', path: '/heat-treatment/rework', icon: <EngineeringIcon /> }
          ]
        }
      ]
    },
    {
      title: 'Periodic Reports',
      icon: <ReportsIcon />,
      section: 'reports',
      subItems: [
        {
          title: 'Reports',
          items: [
            { title: 'Steel Format Report', path: '/reports/steel-format', icon: <DocumentIcon /> },
            { title: 'Raw Material Plan', path: '/reports/rm-plan', icon: <DocumentIcon /> },
            { title: 'Production Grade', path: '/reports/production-grade', icon: <DocumentIcon /> },
            { title: 'Test Certificate', path: '/reports/test-certificate', icon: <DocumentIcon /> },
            { title: 'QFBR Report', path: '/reports/qfbr', icon: <DocumentIcon /> },
            { title: 'Microstructure Report', path: '/reports/microstructure', icon: <DocumentIcon /> },
            { title: 'Periodic Summary', path: '/reports/periodic', icon: <DocumentIcon /> }
          ]
        }
      ]
    },
    {
      title: 'Master Data',
      icon: <SettingsIcon />,
      section: 'masters',
      subItems: [
        {
          title: 'Master Files',
          items: [
            { title: 'Die Master', path: '/masters/dies', icon: <EngineeringIcon /> },
            { title: 'Steel Code Master', path: '/masters/steels', icon: <StorageIcon /> },
            { title: 'Supplier Master', path: '/masters/suppliers', icon: <BusinessIcon /> },
            { title: 'Customer Master', path: '/masters/customers', icon: <PersonIcon /> },
            { title: 'HT Code Master', path: '/masters/ht-codes', icon: <FireIcon /> },
            { title: 'Agent Master', path: '/masters/agents', icon: <PersonIcon /> },
            { title: 'Spec Master', path: '/masters/specifications', icon: <ScienceIcon /> }
          ]
        }
      ]
    }
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          KFL MQC
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.title}>
            {item.path ? (
              // Simple menu item
              <ListItemButton
                selected={isActive(item.path)}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  bgcolor: isActive(item.path) ? 'action.selected' : 'inherit',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            ) : (
              // Expandable menu item
              <>
                <ListItemButton onClick={() => handleSectionToggle(item.section)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.title} />
                  {openSections[item.section] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openSections[item.section]} timeout="auto" unmountOnExit>
                  {item.subItems.map((subItem) => (
                    <Box key={subItem.title} sx={{ pl: 2 }}>
                      <Typography variant="caption" sx={{ pl: 2, color: 'text.secondary', fontWeight: 'bold' }}>
                        {subItem.title}
                      </Typography>
                      <List component="div" disablePadding>
                        {subItem.items.map((subSubItem) => (
                          <ListItemButton
                            key={subSubItem.title}
                            selected={isActive(subSubItem.path)}
                            onClick={() => handleNavigation(subSubItem.path)}
                            sx={{
                              pl: 4,
                              py: 0.5,
                              bgcolor: isActive(subSubItem.path) ? 'action.selected' : 'inherit',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <ListItemIcon sx={{ 
                              minWidth: 36, 
                              color: isActive(subSubItem.path) ? 'primary.main' : 'text.secondary' 
                            }}>
                              {subSubItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={subSubItem.title} 
                              primaryTypographyProps={{ fontSize: '0.875rem' }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Collapse>
              </>
            )}
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            KALYANI FORGE LTD - Materials Quality Control System
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.full_name || user?.username} ({user?.role})
          </Typography>
          <Button color="inherit" onClick={logout} startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;